import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { ONBOARDING_V2 } from '../../components/onboarding/onboardingV2Routes';
import { FondoCordillera } from '../../components/onboarding/marca/FondoCordillera';
import { GuiaCumbre, useDialogo, type Gesto } from '../../components/onboarding/marca/GuiaCumbre';
import {
  ElegirProducto,
  useMercadoLibreEnPiezas,
  useTiendaEnPiezas,
  type FotoSubida,
  type ProductoElegido,
} from '../../components/onboarding/marca/ElegirProducto';
import { RevelarPiezas, type Reaccion as ReaccionALaPieza } from '../../components/onboarding/marca/RevelarPiezas';
import { EjemplosDeFoto } from '../../components/onboarding/marca/EjemplosDeFoto';
import '../../components/onboarding/marca/primerasPiezas.css';
import LoadingAnimation from '../../components/LoadingAnimation';
import { LOADING_CAROUSEL_IMAGES } from '../../components/loading/loadingCarousel';
import { ImagenInvalidaError, prepararImagen } from '../../lib/primeraGeneracion';
import { buscarMarca, type MarcaGuardada } from '../../lib/onboardingMarca/marca';
import { guardarEstado, leerEstado, limpiarEstado } from '../../lib/onboardingMarca/estado';
import { respuestasDe } from '../../lib/onboardingMarca/respuestas';
import { tiendaDeLasPiezas } from '../../lib/onboardingMarca/catalogoDeLaTienda';
import { crearProductoDesdeFoto, type ProductoCreado } from '../../lib/onboardingMarca/productoDesdeFoto';
import type { Reaccion } from '../../lib/onboardingMarca/dialogo';
import {
  dialogoDePiezas,
  OPCIONES_DE_LO_QUE_SE_CREA,
  queSeCrea,
  REACCION_A_LA_FOTO_INVALIDA,
  reaccionAlObjetivo,
  reaccionAlProducto,
} from '../../lib/onboardingMarca/dialogoDePiezas';
import {
  itemsDelLote,
  nombreDelObjetivo,
  objetivoSugerido,
  OPCIONES_DE_PIEZA,
  planDePiezas,
  type ObjetivoDePiezas,
  type PiezaId,
  type UsoDelLogo,
} from '../../lib/onboardingMarca/piezas';
import { esperarPiezas, lanzarPiezas } from '../../lib/onboardingMarca/piezasApi';
import { defaultWatermarkConfig, saveWatermarkConfig, setLastWatermarkChoice } from '../../utils/watermarkConfig';
import { saveMemory } from '../v2/agentMemoryApi';
import { enGuion } from '../../lib/onboardingMarca/guion';

/**
 * A4 · Las primeras piezas (ADR-2026-09-28-onboarding-v2-etapa-1), con Cumbre
 * (ADR-2026-10-02-onboarding-v2-piezas-con-cumbre-y-tienda).
 *
 * Qué se crea arranca por lo que respondió en las preguntas y se puede cambiar; las 3 piezas
 * son 3 variantes de eso (ADR-2026-09-29-onboarding-v2-piezas-a-pedido).
 *
 * El producto sale de una foto que sube la persona (y queda en su catálogo) o, si conectó
 * Tiendanube, de uno de sus productos: la grilla los muestra a medida que el import los trae
 * a Atlas, con cuántos faltan. El motor es el mismo en los dos casos — sólo cambia de dónde
 * sale la variante —: las 3 piezas se lanzan como un lote (1 crédito por imagen entregada) y,
 * con las que salieron, se abre el revelado a pantalla completa. Un refresh no relanza nada:
 * con los ids guardados se reengancha el mismo lote.
 */

type Fase = 'eligiendo' | 'creando' | 'revelando';

function pideMenosMovimiento(): boolean {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

export function PrimerasPiezas() {
  const navigate = useNavigate();
  const inicial = useMemo(() => leerEstado(null), []);
  const companyId = inicial?.companyId ?? null;
  const brandId = inicial?.brandId ?? null;
  const respuestas = inicial?.respuestas;
  const [quieto] = useState(pideMenosMovimiento);

  const [marca, setMarca] = useState<MarcaGuardada | null>(null);
  /** La marca no se pudo consultar (la red): se muestra el aviso con "Volver a intentar". */
  const [marcaNoCargo, setMarcaNoCargo] = useState(false);
  const [intentoDeMarca, setIntentoDeMarca] = useState(0);
  const [foto, setFoto] = useState<FotoSubida | null>(null);
  const [elegido, setElegido] = useState<ProductoElegido | null>(null);
  const [nombreDeLaFoto, setNombreDeLaFoto] = useState(inicial?.lote?.producto ?? '');
  // Por ahora las piezas salen SIN logo y no se puede elegir, hasta mejorar cómo se integra.
  // Un lote ya lanzado (refresh) conserva el uso con el que salió.
  const [uso] = useState<UsoDelLogo>(inicial?.lote?.uso ?? 'sin');
  // Qué crear: las mismas opciones del cuestionario, arrancando por la que eligió. Qué arma el
  // motor con cada una lo decide `objetivoSugerido`, como siempre.
  const [primero, setPrimero] = useState(respuestas?.primero ?? 'anuncios');
  const [otroTexto, setOtroTexto] = useState(respuestas?.otros?.primero ?? '');
  /** Con un lote en vuelo (refresh), lo que se pidió en ese lote; al tocar un chip, se suelta. */
  const [objetivoDelLote, setObjetivoDelLote] = useState<ObjetivoDePiezas | null>(inicial?.lote?.objetivo ?? null);
  const objetivo = useMemo<ObjetivoDePiezas>(
    () => objetivoDelLote ?? (primero === 'otro' ? { tipo: 'otro', otro: otroTexto.trim() } : objetivoSugerido(primero)),
    [objetivoDelLote, primero, otroTexto],
  );
  const [queCreaAlEntrar] = useState(() => queSeCrea(respuestas?.primero ?? 'anuncios', respuestas?.otros?.primero ?? ''));
  const [fase, setFase] = useState<Fase>(inicial?.lote ? 'creando' : 'eligiendo');
  /** La pantalla de carga llegó a 100% y se fue: recién ahí se abre el revelado. */
  const [cargaTerminada, setCargaTerminada] = useState(false);
  const [urls, setUrls] = useState<Partial<Record<PiezaId, string>>>({});
  const [fallidas, setFallidas] = useState<PiezaId[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [reaccion, setReaccion] = useState<Reaccion | null>(null);
  const [gesto, setGesto] = useState<Gesto | null>(null);
  const cuenta = useRef(0);
  const producto = useRef<ProductoCreado | null>(null);

  // Cada canal se mira sólo si dijo que vende por él (y no hay un lote en vuelo); si marcó los dos,
  // se muestra el primero que marcó (ADR-2026-10-19-mercadolibre-en-las-primeras-piezas).
  const canales = respuestasDe(respuestas, 'canal');
  const deTiendanube = useTiendaEnPiezas({ companyId, brandId, activo: canales.includes('tiendanube') && !inicial?.lote });
  const deMercadoLibre = useMercadoLibreEnPiezas({ companyId, brandId, activo: canales.includes('mercadolibre') && !inicial?.lote });
  const tienda = tiendaDeLasPiezas(canales, { tiendanube: deTiendanube, mercadolibre: deMercadoLibre });

  useEffect(() => {
    if (!companyId || !brandId) {
      navigate(companyId ? ONBOARDING_V2.setupMarca : ONBOARDING_V2.setup, { replace: true });
      return;
    }
    let vivo = true;
    setMarcaNoCargo(false);
    void buscarMarca(brandId).then((r) => {
      if (!vivo) return;
      if (r.estado === 'ok') return setMarca(r.marca);
      // Una marca que no existe para esta sesión (borrada, o de otra cuenta) deja el
      // onboarding guardado sin sentido: se arranca de cero en vez de quedar con "Crear"
      // deshabilitado y sin salida. Si fue la red, se ofrece reintentar
      // (review Greptile #164, ADR-2026-10-07-una-marca-que-no-existe-reinicia-el-onboarding).
      if (r.estado === 'no_existe') {
        limpiarEstado();
        navigate(ONBOARDING_V2.setup, { replace: true });
        return;
      }
      setMarcaNoCargo(true);
    });
    return () => {
      vivo = false;
    };
  }, [companyId, brandId, navigate, inicial?.lote, intentoDeMarca]);

  useEffect(
    () => () => {
      if (foto) URL.revokeObjectURL(foto.previewUrl);
    },
    [foto],
  );

  /** El nombre que va a los prompts: el del producto de la tienda, o el que escribió para la foto. */
  const nombreProducto = elegido?.origen === 'tienda' ? elegido.producto.nombre : nombreDeLaFoto;

  const planes = useMemo(
    () =>
      marca
        ? planDePiezas(
            {
              nombre: marca.nombre,
              rubro: marca.rubro,
              colores: marca.colores,
              tipografias: marca.tipografias,
              analisis: marca.analisis,
              logoUrl: marca.logoUrl,
            },
            nombreProducto,
            uso,
            objetivo,
          )
        : [],
    [marca, nombreProducto, uso, objetivo],
  );

  // ── Cumbre ──
  // Mientras se averigua si la tienda está conectada, se asume lo que respondió: si no lo
  // está, el globo se corrige (casi siempre antes de empezar a escribir).
  const conTienda = tienda.estado === 'conectada' || tienda.estado === 'revisando';
  const tramos = useMemo(
    () => dialogoDePiezas({ queCrea: queCreaAlEntrar, conTienda, pidioVideo: respuestas?.primero === 'videos' }),
    [queCreaAlEntrar, conTienda, respuestas?.primero],
  );
  const dialogo = useDialogo(tramos, { instantaneo: quieto, arrancarEn: 900 });
  // El contenido espera a que Cumbre termine la primera vez; después no se esconde más.
  const [mostrar, setMostrar] = useState(quieto);
  useEffect(() => {
    if (dialogo.terminado) setMostrar(true);
  }, [dialogo.terminado]);

  const decir = (r: Reaccion, tipo: Gesto['tipo']) => {
    setReaccion(r);
    setGesto((g) => ({ tipo, n: (g?.n ?? 0) + 1 }));
  };

  const esperar = useCallback(
    async (ejecuciones: Partial<Record<PiezaId, string>>) => {
      if (!companyId) return;
      const llegadas: PiezaId[] = [];
      await esperarPiezas({
        ejecuciones,
        companyId,
        onPieza: (p) => {
          llegadas.push(p.id);
          setUrls((u) => ({ ...u, [p.id]: p.url }));
        },
        onFallo: (id) => setFallidas((f) => (f.includes(id) ? f : [...f, id])),
      });
      if (llegadas.length > 0) setFase('revelando');
      else {
        setError('No pudimos crear tus piezas esta vez. Intenta de nuevo.');
        setFase('eligiendo');
      }
    },
    [companyId],
  );

  // Refresh con un lote en vuelo: se reengancha, no se relanza.
  const reenganchado = useRef(false);
  useEffect(() => {
    if (reenganchado.current || !inicial?.lote) return;
    reenganchado.current = true;
    void esperar(inicial.lote.ejecuciones);
  }, [inicial?.lote, esperar]);

  const elegirFoto = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setError(null);
    try {
      const lista = await prepararImagen(file);
      setFoto(lista);
      setElegido({ origen: 'foto' });
      producto.current = null;
      cuenta.current += 1;
      decir(reaccionAlProducto('', cuenta.current), 'festeja');
    } catch (err) {
      setError(err instanceof ImagenInvalidaError ? err.message : 'No pudimos leer esa foto. Intenta con otra.');
      decir(REACCION_A_LA_FOTO_INVALIDA, 'duda');
    }
  };

  const elegirProducto = (p: ProductoElegido) => {
    setError(null);
    if (p.origen === 'foto' && !foto) return;
    setElegido(p);
    cuenta.current += 1;
    decir(reaccionAlProducto(p.origen === 'tienda' ? p.producto.nombre : '', cuenta.current), 'festeja');
  };

  const elegirQueCrear = (id: string) => {
    const cambio = id !== primero;
    setPrimero(id);
    setObjetivoDelLote(null);
    if (cambio && id !== 'otro') {
      cuenta.current += 1;
      decir(reaccionAlObjetivo(id, otroTexto, cuenta.current), 'festeja');
    }
  };

  const faltaObjetivo = objetivo.tipo === 'otro' && !objetivo.otro?.trim();
  const hayProducto = elegido?.origen === 'tienda' || (elegido?.origen === 'foto' && !!foto);

  const crear = async () => {
    if (!companyId || !brandId || !marca || !elegido || !hayProducto || faltaObjetivo || fase !== 'eligiendo') return;
    setError(null);
    setFallidas([]);
    setUrls({});
    setCargaTerminada(false);
    setFase('creando');
    let varianteId: string;
    if (elegido.origen === 'tienda' && elegido.producto.varianteId) {
      // Ya está en Atlas (lo trajo el import): se genera con su variante, como con una foto.
      varianteId = elegido.producto.varianteId;
    } else {
      if (!foto) return;
      try {
        // El producto se crea una vez: si el lanzamiento falla y se reintenta, se reusa.
        producto.current ??= await crearProductoDesdeFoto({
          file: foto.file,
          nombre: nombreDeLaFoto,
          companyId,
          brandId,
        });
      } catch {
        setError('No pudimos guardar la foto de tu producto. Intenta de nuevo.');
        setFase('eligiendo');
        return;
      }
      varianteId = producto.current.varianteId;
    }
    const lanzamiento = await lanzarPiezas(itemsDelLote(planes, { varianteId, logoUrl: marca.logoUrl, uso }));
    if (!lanzamiento.ok) {
      setError(lanzamiento.error);
      setFase('eligiendo');
      return;
    }
    guardarEstado({
      companyId,
      lote: { ejecuciones: lanzamiento.ejecuciones, uso, producto: nombreProducto, objetivo, lanzadoEn: Date.now() },
    });
    if (uso === 'agua' && !enGuion()) {
      const base = defaultWatermarkConfig(marca.nombre);
      void saveWatermarkConfig(brandId, { ...base, text: { ...base.text, visible: false } });
      setLastWatermarkChoice('con');
    }
    await esperar(lanzamiento.ejecuciones);
  };

  const terminar = (reacciones: Partial<Record<PiezaId, ReaccionALaPieza>>) => {
    if (companyId && !enGuion()) {
      const etiqueta = (id: PiezaId) => planes.find((p) => p.id === id)?.etiqueta ?? id;
      const gustaron = (Object.keys(reacciones) as PiezaId[]).filter((id) => reacciones[id] === 'me_gusta').map(etiqueta);
      const noVan = (Object.keys(reacciones) as PiezaId[]).filter((id) => reacciones[id] === 'no_va').map(etiqueta);
      // Qué pidió generar se guarda SIEMPRE: al agente le sirve para proponer lo mismo
      // después. Los gustos, sólo si el revelado los recogió (hoy no pide reacciones).
      const texto = [
        `Para sus primeras piezas pidió: ${nombreDelObjetivo(objetivo)}.`,
        gustaron.length ? `En sus primeras piezas le gustaron: ${gustaron.join(', ')}.` : '',
        noVan.length ? `No le gustaron: ${noVan.join(', ')}.` : '',
      ]
        .filter(Boolean)
        .join(' ');
      void saveMemory(companyId, texto, 'primeras_piezas');
    }
    navigate(ONBOARDING_V2.setupListo, {
      state: { piezas: planes.filter((p) => urls[p.id]).map((p) => ({ etiqueta: p.etiqueta, url: urls[p.id] })) },
    });
  };

  const listas = planes.filter((p) => urls[p.id]);
  const creando = fase === 'creando';
  const eligiendo = fase === 'eligiendo';
  // Debajo de los chips, qué sale: para "Videos", que por ahora son imágenes.
  const detalleDelObjetivo =
    primero === 'videos'
      ? `Los videos los creas después, desde Atlas. Por ahora armo ${queSeCrea('videos', '')}.`
      : OPCIONES_DE_PIEZA.find((o) => o.id === objetivo.tipo)?.detalle;

  return (
    <div className="relative isolate flex min-h-[calc(100dvh-var(--om-barra))] flex-col">
      <FondoCordillera quieto={quieto} />

      <div className="mx-auto flex w-full max-w-[640px] flex-col gap-5 px-4 pb-10 pt-6 md:gap-6 md:pt-8">
        <GuiaCumbre
          tramos={tramos}
          letras={dialogo.letras}
          hablando={dialogo.hablando}
          terminado={dialogo.terminado}
          onCompletar={dialogo.completar}
          reaccion={reaccion}
          animo={creando ? 'piensa' : tienda.estado === 'conectada' && tienda.carga === 'cargando' ? 'piensa' : 'escucha'}
          gesto={gesto}
          instantaneo={quieto}
        />

        {mostrar && eligiendo && (
          <>
            {/* Primero el producto y después qué crear con él. */}
            <section className="om-seccion" style={{ ['--orden' as string]: 0 }} aria-labelledby="om-tu-producto">
              <div className="om-seccion__cabeza">
                <span className="om-seccion__icono material-symbols-rounded" aria-hidden="true">
                  inventory_2
                </span>
                {/* Con tienda se elige uno de los suyos; sin tienda hay que sumarlo con una foto. */}
                <h2 id="om-tu-producto" className="om-seccion__titulo">
                  {conTienda ? 'Tu producto' : 'Añadir producto'}
                </h2>
                {tienda.estado === 'conectada' && tienda.tienda && <span className="om-seccion__nota">{tienda.tienda}</span>}
              </div>
              {/* Sin tienda hay que subir una foto: cómo tiene que ser, con los ejemplos del
                  rubro, arriba del botón. Se va cuando ya subió una. */}
              {!conTienda && !foto && <EjemplosDeFoto rubro={marca?.rubro ?? ''} />}
              <ElegirProducto
                tienda={tienda}
                elegido={elegido}
                foto={foto}
                nombreDeLaFoto={nombreDeLaFoto}
                onElegir={elegirProducto}
                onFoto={(e) => void elegirFoto(e)}
                onNombreDeLaFoto={setNombreDeLaFoto}
              />
            </section>

            <section className="om-seccion" style={{ ['--orden' as string]: 1 }} aria-labelledby="om-que-creamos">
              <div className="om-seccion__cabeza">
                <span className="om-seccion__icono material-symbols-rounded" aria-hidden="true">
                  auto_awesome
                </span>
                <h2 id="om-que-creamos" className="om-seccion__titulo">
                  Qué vamos a crear
                </h2>
              </div>
              <div className="om-chips" role="radiogroup" aria-labelledby="om-que-creamos">
                {[...OPCIONES_DE_LO_QUE_SE_CREA, { id: 'otro', label: 'Otro', icono: 'edit' }].map((o) => (
                  <button
                    key={o.id}
                    type="button"
                    role="radio"
                    aria-checked={primero === o.id}
                    onClick={() => elegirQueCrear(o.id)}
                    className={`om-chip ${primero === o.id ? 'is-elegido' : ''}`}
                  >
                    <span className="material-symbols-rounded" aria-hidden="true">
                      {o.icono}
                    </span>
                    {o.label}
                  </button>
                ))}
              </div>
              {primero === 'otro' ? (
                <input
                  id="om-objetivo-otro"
                  autoFocus
                  value={otroTexto}
                  onChange={(e) => {
                    setOtroTexto(e.target.value);
                    setObjetivoDelLote(null);
                  }}
                  placeholder="Ej: Un flyer para el Día del Padre"
                  aria-label="Qué te gustaría crear"
                  className="om-input"
                />
              ) : (
                detalleDelObjetivo && <p className="om-chips__detalle">{detalleDelObjetivo}</p>
              )}
            </section>

            {/* "Tu logo en las piezas" salió por ahora: las piezas van sin logo. */}
          </>
        )}

        {/* Mientras se crean, la pantalla de carga de siempre de Atlas (overlay en desktop,
            pantalla completa en el teléfono). Al terminar la barra llega a 100% y se abre el
            revelado (ADR-2026-09-29-onboarding-v2-carga-de-atlas-en-las-piezas). */}
        {(creando || (fase === 'revelando' && !cargaTerminada)) && (
          <LoadingAnimation
            message={`Creando las piezas de ${marca?.nombre || 'tu marca'}`}
            subMessage={`${listas.length} de ${planes.length || 3} listas. No cierres esta pestaña.`}
            carouselImages={LOADING_CAROUSEL_IMAGES}
            carouselTitle="Esto es lo que puedes crear con Atlas"
            durationMs={90_000}
            completeNow={fase === 'revelando'}
            onCompleted={() => setCargaTerminada(true)}
          />
        )}
        {fase === 'revelando' && cargaTerminada && planes.length > 0 && (
          <RevelarPiezas
            piezas={planes.map((plan) => ({ plan, url: urls[plan.id], fallo: fallidas.includes(plan.id) }))}
            logoAgua={uso === 'agua' ? (marca?.logoUrl ?? null) : null}
            creando={creando}
            onTerminar={terminar}
          />
        )}
      </div>

      {mostrar && eligiendo && (
        <div className="om-barra">
          <div className="om-barra__dentro">
            {/* En el teléfono, el botón primero y el aviso debajo; en desktop, el aviso a su izquierda. */}
            <button
              type="button"
              onClick={() => void crear()}
              disabled={!marca || !hayProducto || faltaObjetivo}
              className="om-boton md:order-last md:w-auto md:px-10"
            >
              Crear imágenes
            </button>
            {marcaNoCargo && !error ? (
              <div role="alert" className="om-barra__aviso flex flex-col gap-1 text-[#991B1B] md:text-right">
                No pudimos cargar tu marca. Revisa tu conexión.
                <button
                  type="button"
                  onClick={() => setIntentoDeMarca((n) => n + 1)}
                  className="om-canal__despues self-start md:self-end"
                >
                  Volver a intentar
                </button>
              </div>
            ) : error ? (
              <div role="alert" className="om-barra__aviso flex flex-col gap-1 text-[#991B1B] md:text-right">
                {error}
                {/* Con cualquier error, no sólo sin créditos: una tanda que falla (429, 5xx, todas
                    las piezas caídas) dejaba "Crear imágenes" como única salida, y la marca ya
                    está confirmada (ADR-2026-10-12-el-onboarding-de-marca-tras-prenderlo). */}
                <button
                  type="button"
                  onClick={() => navigate(ONBOARDING_V2.setupListo)}
                  className="om-canal__despues self-start md:self-end"
                >
                  Seguir sin piezas
                </button>
              </div>
            ) : (
              <p className="om-barra__aviso text-center md:text-right">
                {faltaObjetivo
                  ? 'Cuéntanos qué te gustaría crear.'
                  : !hayProducto
                    ? conTienda
                      ? 'Elige uno de tus productos o sube una foto.'
                      : 'Sube una foto de tu producto para crear las piezas.'
                    : 'Usa 3 créditos, uno por pieza.'}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default PrimerasPiezas;
